import {
  CONSULTATION_WEEKDAY_KEYS,
  consultationIsoDateSchema,
  consultationTimeSchema,
  consultationTimeToMinutes,
  isValidIanaTimeZone,
} from '@forgd/shared/schemas/consultation'
import { initContract } from '@ts-rest/core'
import { z } from 'zod'

const c = initContract()

export const consultationDayHoursSchema = z
  .object({
    enabled: z.boolean(),
    start: consultationTimeSchema,
    end: consultationTimeSchema,
  })
  .superRefine((value, context) => {
    if (!value.enabled) {
      return
    }
    const startMinutes = consultationTimeToMinutes(value.start)
    const endMinutes = consultationTimeToMinutes(value.end)
    if (startMinutes === null || endMinutes === null) {
      return
    }
    if (startMinutes >= endMinutes) {
      context.addIssue({
        code: 'custom',
        message: 'Start must be before end',
        path: ['end'],
      })
    }
  })

export const consultationWeekdayHoursSchema = z.object({
  monday: consultationDayHoursSchema,
  tuesday: consultationDayHoursSchema,
  wednesday: consultationDayHoursSchema,
  thursday: consultationDayHoursSchema,
  friday: consultationDayHoursSchema,
  saturday: consultationDayHoursSchema,
  sunday: consultationDayHoursSchema,
})

export const consultationBookingPolicySchema = z
  .object({
    timeZone: z.string().refine(isValidIanaTimeZone, 'Invalid IANA timezone'),
    meetingDurationMinutes: z.number().int().min(15).max(240),
    slotStepMinutes: z.number().int().min(5).max(240),
    weekdayHours: consultationWeekdayHoursSchema,
    blackoutDates: z.array(consultationIsoDateSchema).superRefine((dates, context) => {
      const unique = new Set(dates)
      if (unique.size !== dates.length) {
        context.addIssue({
          code: 'custom',
          message: 'Blackout dates must be unique',
        })
      }
    }),
  })
  .superRefine((value, context) => {
    if (value.slotStepMinutes < value.meetingDurationMinutes) {
      context.addIssue({
        code: 'custom',
        message: 'Slot step must be greater than or equal to meeting duration',
        path: ['slotStepMinutes'],
      })
    }

    for (const day of CONSULTATION_WEEKDAY_KEYS) {
      const hours = value.weekdayHours[day]
      if (!hours.enabled) {
        continue
      }
      const startMinutes = consultationTimeToMinutes(hours.start)
      const endMinutes = consultationTimeToMinutes(hours.end)
      if (startMinutes === null || endMinutes === null) {
        continue
      }
      if (endMinutes - startMinutes < value.meetingDurationMinutes) {
        context.addIssue({
          code: 'custom',
          message: `${day} window must fit at least one meeting`,
          path: ['weekdayHours', day, 'end'],
        })
      }
    }
  })

const apiMessageErrorSchema = z.object({
  message: z.string(),
})

export const googleCalendarWritableSchema = z.object({
  id: z.string().min(1),
  summary: z.string().min(1),
  timeZone: z.string().nullable(),
  primary: z.boolean(),
})

const connectionBaseFields = {
  googleAccountEmail: z.string().email().nullable(),
  calendarId: z.string().nullable(),
  calendarName: z.string().nullable(),
  calendarTimeZone: z.string().nullable(),
  connectedAt: z.string().datetime().nullable(),
  lastCheckedAt: z.string().datetime().nullable(),
  lastError: z.string().nullable(),
  policy: consultationBookingPolicySchema,
}

export const googleCalendarIntegrationStatusSchema = z.discriminatedUnion('state', [
  z.object({
    state: z.literal('disconnected'),
    ...connectionBaseFields,
    googleAccountEmail: z.null(),
    calendarId: z.null(),
    calendarName: z.null(),
    calendarTimeZone: z.null(),
    connectedAt: z.null(),
  }),
  z.object({
    state: z.literal('calendar-selection-required'),
    ...connectionBaseFields,
    googleAccountEmail: z.string().email(),
    calendarId: z.null(),
    calendarName: z.null(),
    calendarTimeZone: z.null(),
    connectedAt: z.string().datetime(),
  }),
  z.object({
    state: z.literal('connected'),
    ...connectionBaseFields,
    googleAccountEmail: z.string().email(),
    calendarId: z.string().min(1),
    calendarName: z.string().min(1),
    calendarTimeZone: z.string().nullable(),
    connectedAt: z.string().datetime(),
  }),
  z.object({
    state: z.literal('error'),
    ...connectionBaseFields,
    lastError: z.string().min(1),
  }),
])

export const googleCalendarSettingsBodySchema = z.object({
  calendarId: z.string().min(1),
  policy: consultationBookingPolicySchema,
})

export const googleCalendarOauthStartBodySchema = z.object({})

export const googleCalendarOauthStartResponseSchema = z.object({
  authorizationUrl: z.string().url(),
})

export const googleCalendarTestResponseSchema = z.object({
  ok: z.literal(true),
  lastCheckedAt: z.string().datetime(),
})

export const googleCalendarDisconnectResponseSchema = z.object({
  ok: z.literal(true),
})

const upcomingEventBase = z.object({
  id: z.string().min(1),
  start: z.string().min(1),
  end: z.string().min(1),
  allDay: z.boolean(),
})

export const googleCalendarUpcomingEventSchema = z.discriminatedUnion('kind', [
  upcomingEventBase.extend({
    kind: z.literal('consultation'),
    title: z.string(),
    consultationType: z.enum(['project', 'market-maker']),
    projectName: z.string().nullable(),
    attendeeEmail: z.string().nullable(),
    attendeeName: z.string().nullable(),
    meetLink: z.string().nullable(),
    htmlLink: z.string().nullable(),
  }),
  upcomingEventBase.extend({
    kind: z.literal('busy'),
  }),
])

export const googleCalendarUpcomingEventsResponseSchema = z.object({
  events: z.array(googleCalendarUpcomingEventSchema),
  lastCheckedAt: z.string().datetime().nullable(),
})

export const adminGoogleCalendarContract = c.router({
  getStatus: {
    method: 'GET',
    path: '/admin/integrations/google-calendar',
    responses: {
      200: googleCalendarIntegrationStatusSchema,
    },
    summary: 'Get Google Calendar integration status and booking policy',
  },
  startOauth: {
    method: 'POST',
    path: '/admin/integrations/google-calendar/oauth/start',
    body: googleCalendarOauthStartBodySchema,
    responses: {
      200: googleCalendarOauthStartResponseSchema,
      400: apiMessageErrorSchema,
      503: apiMessageErrorSchema,
    },
    summary: 'Start Google Calendar OAuth for the initiating admin',
  },
  listCalendars: {
    method: 'GET',
    path: '/admin/integrations/google-calendar/calendars',
    responses: {
      200: z.object({
        calendars: z.array(googleCalendarWritableSchema),
      }),
      400: apiMessageErrorSchema,
      503: apiMessageErrorSchema,
    },
    summary: 'List writable calendars for the connected Google account',
  },
  listUpcomingEvents: {
    method: 'GET',
    path: '/admin/integrations/google-calendar/upcoming-events',
    responses: {
      200: googleCalendarUpcomingEventsResponseSchema,
      400: apiMessageErrorSchema,
      503: apiMessageErrorSchema,
    },
    summary: 'List upcoming events from the selected calendar',
  },
  saveSettings: {
    method: 'PUT',
    path: '/admin/integrations/google-calendar/settings',
    body: googleCalendarSettingsBodySchema,
    responses: {
      200: googleCalendarIntegrationStatusSchema,
      400: apiMessageErrorSchema,
      503: apiMessageErrorSchema,
    },
    summary: 'Save selected calendar and booking policy',
  },
  testConnection: {
    method: 'POST',
    path: '/admin/integrations/google-calendar/test',
    body: z.object({}),
    responses: {
      200: googleCalendarTestResponseSchema,
      400: apiMessageErrorSchema,
      503: apiMessageErrorSchema,
    },
    summary: 'Validate Google Calendar access with a non-mutating FreeBusy query',
  },
  disconnect: {
    method: 'POST',
    path: '/admin/integrations/google-calendar/disconnect',
    body: z.object({}),
    responses: {
      200: googleCalendarDisconnectResponseSchema,
    },
    summary: 'Revoke Google grant and clear connection credentials',
  },
})

export type AdminGoogleCalendarContractType = typeof adminGoogleCalendarContract
export type GoogleCalendarIntegrationStatus = z.infer<typeof googleCalendarIntegrationStatusSchema>
export type ConsultationBookingPolicy = z.infer<typeof consultationBookingPolicySchema>
export type ConsultationDayHours = z.infer<typeof consultationDayHoursSchema>
export type GoogleCalendarUpcomingEvent = z.infer<typeof googleCalendarUpcomingEventSchema>
